import type { FC } from "react";
import { useMemo } from "react";

import { Map } from "@similarweb/pro-logic-components";

import { useMapGeoJson } from "app/common/hooks";
import { MetricContainer, MetricsRow } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { CircularProgress } from "pro-features/components/Chart/src/components/annotations/Components/CircularProgress";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import { MiniFlexTable } from "app/components/React/Table/FlexTable/Mini/MiniFlexTable";
import { getHighchartsConfig } from "app/components/TrafficTopCountries/getHighchartsConfig";
import { CardBodyWrapper, TitleContainerStyled } from "app/components/TrafficTopCountries/styles";
import { WidgetFooter } from "app/components/widget/WidgetFooter";
import { translate } from "app/filters/i18nFilter";
import { countryByIdFilter } from "app/filters/ngFilters";
import { NoDataWrapper } from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";
import type { CountryTrafficShare } from "app/pages/website-performance/components/TopCountries/type";
import wwoTrafficTopCountriesTableConfig from "app/pages/website-performance/components/TopCountries/WWOTrafficTopCountriesTableConfig";
import DurationService from "app/services/DurationService";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";

interface IctaHref {
    href: string;
    target: string;
}

interface ITextKeys {
    titleKey: string;
    titleTooltipKey: string;
}

interface ITrafficTopCountries {
    params: { duration: string; webSource: string };
    isLoading: boolean;
    countriesTrafficShare: CountryTrafficShare;

    isCompare?: boolean;
    ctaHref: IctaHref;
    unlockHook?: any;
    textKeys: ITextKeys;
    subtitleFilters?: Array<unknown>;
    hideFooter?: boolean;
    onDateClick?: VoidFunction;
    onDateHover?: VoidFunction;
    onWebSourceHover?: VoidFunction;
}

export const TrafficTopCountries: FC<ITrafficTopCountries> = (props) => {
    const {
        textKeys,
        isLoading,
        countriesTrafficShare,
        isCompare = false,
        unlockHook,
        ctaHref,
        params,
        hideFooter = false,
        onDateClick,
        onDateHover,
        onWebSourceHover,
    } = props;
    const { duration, webSource } = params;
    const { titleKey, titleTooltipKey } = textKeys;
    const { data: mapGeoJson, isLoading: isMapGeoJsonLoading } = useMapGeoJson();
    const { from, to } = useMemo(() => DurationService.getDurationData(duration).forAPI, [duration]);
    const subtitleFilters = props.subtitleFilters ?? [
        {
            filter: "date",
            value: {
                from,
                to,
            },
            onClick: onDateClick,
            onMouseEnter: onDateHover,
        },
        {
            filter: "webSource",
            value: webSource,
            onMouseEnter: onWebSourceHover,
        },
    ];

    const tableConfig = useMemo(() => wwoTrafficTopCountriesTableConfig(isCompare), [isCompare]);

    const mapData = useMemo(() => {
        const countryById = countryByIdFilter();
        return (
            countriesTrafficShare?.map((item) => ({
                "hc-key": countryById(item.country).code,
                value: item.share,
            })) || []
        );
    }, [countriesTrafficShare]);

    const chartConfig = useMemo(() => getHighchartsConfig(), []);

    const tableKey = useMemo(() => {
        if (!countriesTrafficShare?.length) {
            return "table-empty";
        }

        const firstCountry = countriesTrafficShare[0];
        return `table-${firstCountry.country}-${firstCountry.share}-${firstCountry.change}`;
    }, [countriesTrafficShare]);

    return (
        <MetricsRow>
            <MetricContainer
                width="100%"
                height="372px"
                padding="0px"
            >
                <TitleContainerStyled>
                    <BoxTitle tooltip={translate(titleTooltipKey)}>{translate(titleKey)}</BoxTitle>
                    <StyledBoxSubtitle>
                        <BoxSubtitle filters={subtitleFilters} />
                    </StyledBoxSubtitle>
                </TitleContainerStyled>
                <CardBodyWrapper>
                    {isLoading ? (
                        <CircularProgress />
                    ) : !countriesTrafficShare.length ? (
                        <NoDataWrapper>
                            <NoDataLandscape
                                title="mmx.channelsoverview.bar.chart.no.data.title"
                                subtitle="mmx.channelsoverview.bar.chart.no.data.subtitle"
                            />
                        </NoDataWrapper>
                    ) : (
                        <>
                            <Map
                                config={chartConfig}
                                mapData={mapGeoJson}
                                data={mapData}
                                isLoading={isMapGeoJsonLoading}
                                style={{
                                    height: "100%",
                                }}
                            />
                            <MiniFlexTable
                                key={tableKey}
                                tableData={{ Records: countriesTrafficShare }}
                                tableColumns={tableConfig.columns}
                                tableOptions={tableConfig.options}
                            />
                        </>
                    )}
                </CardBodyWrapper>
                {hideFooter ? null : (
                    <WidgetFooter
                        {...ctaHref}
                        text="wwo.geography.button"
                        trackingLabel="Geography/Top 5 Countries/see more countries"
                        unlockHook={unlockHook}
                    />
                )}
            </MetricContainer>
        </MetricsRow>
    );
};
